import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Hash, Save, RefreshCw, Wand2 } from 'lucide-react';
import { admin as adminApi, membershipIds } from '../utils/api';

/**
 * MembershipIdSettings — admin panel for the roadmap item #21
 * membership/account ID feature.
 *
 * Backed by KV rows in `system_settings` (see migration 043) plus the
 * dedicated /api/admin/membership-ids/* endpoints for preview and
 * backfill. Save flows through the generic /admin/settings PUT so a
 * single round-trip persists every field.
 *
 * The format dropdown drives a live preview rendered server-side
 * (server applies the same formatter as real issuance, so admins see
 * exactly what new clients will get).
 */

const FORMAT_LABELS = {
  numeric: 'Numeric (e.g. 000001)',
  prefix_numeric: 'Prefix + numeric (e.g. M-000001)',
  alpha: 'Random letters (e.g. ABCDEF)',
  alphanumeric: 'Random letters + numbers (e.g. A2C4E6)',
};

const DEFAULTS = {
  enabled: false,
  auto_assign: true,
  format: 'numeric',
  prefix: '',
  suffix: '',
  length: 6,
  next_seq: 1,
};

const MembershipIdSettings = () => {
  const [settings, setSettings] = useState({ ...DEFAULTS });
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  // Re-fetch the server preview whenever the format-affecting fields
  // change. Cheaper than building a JS-side renderer that has to stay
  // in lockstep with the PHP one.
  useEffect(() => {
    if (loading) return;
    refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.format, settings.prefix, settings.suffix, settings.length, settings.next_seq]);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getSettings();
      const rows = res.data || [];
      const get = (key, fallback) => {
        const r = rows.find((x) => x.setting_key === key);
        return r ? r.setting_value : fallback;
      };
      const isTrue = (v) => ['1', 'true', 'yes', 'on'].includes(String(v).toLowerCase());
      const next = {
        enabled: isTrue(get('membership_ids_enabled', 'false')),
        auto_assign: isTrue(get('membership_id_auto_assign', 'true')),
        format: get('membership_id_format', 'numeric'),
        prefix: get('membership_id_prefix', ''),
        suffix: get('membership_id_suffix', ''),
        length: parseInt(get('membership_id_length', '6'), 10) || 6,
        next_seq: parseInt(get('membership_id_next_seq', '1'), 10) || 1,
      };
      setSettings(next);
    } catch (e) {
      console.error('Load membership ID settings:', e);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const refreshPreview = async () => {
    try {
      const res = await membershipIds.preview();
      setPreview(res.data?.preview || '');
    } catch (e) {
      // non-fatal
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = [
        { key: 'membership_ids_enabled',    value: settings.enabled ? 'true' : 'false' },
        { key: 'membership_id_auto_assign', value: settings.auto_assign ? 'true' : 'false' },
        { key: 'membership_id_format',      value: settings.format },
        { key: 'membership_id_prefix',      value: settings.prefix || '' },
        { key: 'membership_id_suffix',      value: settings.suffix || '' },
        { key: 'membership_id_length',      value: String(Math.max(1, Math.min(32, settings.length || 6))) },
        { key: 'membership_id_next_seq',    value: String(Math.max(1, settings.next_seq || 1)) },
      ];
      await adminApi.updateSettings(payload);
      toast.success('Membership ID settings saved');
      refreshPreview();
    } catch (e) {
      console.error('Save membership ID settings:', e);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleBackfill = async () => {
    if (!settings.enabled) {
      toast.error('Enable membership IDs before running a backfill');
      return;
    }
    if (!window.confirm('Issue a membership ID to every client that does not have one yet?')) return;
    setBackfilling(true);
    try {
      const res = await membershipIds.backfill();
      toast.success(`Assigned ${res.data?.assigned ?? 0} of ${res.data?.tot ?? res.data?.total ?? 0} client(s)`);
      refreshPreview();
    } catch (e) {
      console.error('Backfill error:', e);
      toast.error(e.response?.data?.error || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  const isSequential = settings.format === 'numeric' || settings.format === 'prefix_numeric';
  const isRandom = settings.format === 'alpha' || settings.format === 'alphanumeric';

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Hash className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Membership IDs</h2>
      </div>

      <p className="text-gray-600 mb-4">
        Issue a printable account number to each client &mdash; useful for clubs,
        sports leagues, gyms, and any business that prints membership cards.
        New signups are auto-assigned when both toggles below are on.
      </p>

      {/* Master toggles */}
      <div className="space-y-3 mb-6">
        <label className="flex items-center space-x-3 cursor-pointer select-none">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          />
          <span className="text-gray-800 font-medium">Enable membership IDs</span>
        </label>
        <label className={`flex items-center space-x-3 cursor-pointer select-none ${!settings.enabled ? 'opacity-50' : ''}`}>
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={settings.auto_assign}
            disabled={!settings.enabled}
            onChange={(e) => setSettings({ ...settings, auto_assign: e.target.checked })}
          />
          <span className="text-gray-800">Auto-assign to new client signups</span>
        </label>
      </div>

      {/* Format controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
          <select
            className="input"
            value={settings.format}
            onChange={(e) => setSettings({ ...settings, format: e.target.value })}
            disabled={!settings.enabled}
          >
            {Object.entries(FORMAT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Length {isRandom ? '(characters)' : '(digits, zero-padded)'}
          </label>
          <input
            type="number"
            className="input"
            min={1}
            max={32}
            value={settings.length}
            onChange={(e) => setSettings({ ...settings, length: parseInt(e.target.value, 10) || 1 })}
            disabled={!settings.enabled}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prefix (optional)</label>
          <input
            type="text"
            className="input"
            maxLength={16}
            placeholder="e.g. M-"
            value={settings.prefix}
            onChange={(e) => setSettings({ ...settings, prefix: e.target.value })}
            disabled={!settings.enabled}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Suffix (optional)</label>
          <input
            type="text"
            className="input"
            maxLength={16}
            placeholder="e.g. -A"
            value={settings.suffix}
            onChange={(e) => setSettings({ ...settings, suffix: e.target.value })}
            disabled={!settings.enabled}
          />
        </div>

        {isSequential && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Next sequence number
            </label>
            <input
              type="number"
              className="input"
              min={1}
              value={settings.next_seq}
              onChange={(e) => setSettings({ ...settings, next_seq: parseInt(e.target.value, 10) || 1 })}
              disabled={!settings.enabled}
            />
            <p className="text-xs text-gray-500 mt-1">
              The next sequential ID issued will use this value, then auto-increment.
              Bump it forward to skip a range; lowering it risks collisions.
            </p>
          </div>
        )}
      </div>

      {/* Live preview */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 flex items-center gap-3">
        <Wand2 size={18} className="text-gray-500" />
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500">Next ID will look like</div>
          <div className="text-lg font-mono font-semibold">{preview || '—'}</div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center gap-2"
        >
          <Save size={16} />
          {saving ? 'Saving…' : 'Save Settings'}
        </button>

        <button
          onClick={handleBackfill}
          disabled={backfilling || !settings.enabled}
          className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
          title={!settings.enabled ? 'Enable membership IDs first' : ''}
        >
          <RefreshCw size={16} className={backfilling ? 'animate-spin' : ''} />
          {backfilling ? 'Backfilling…' : 'Assign IDs to existing clients'}
        </button>
      </div>
    </div>
  );
};

export default MembershipIdSettings;
